// Componentes base del lenguaje clean-digital. Todos usan controles nativos (button, a, input, dialog)
// para que el teclado, los lectores de pantalla y el foco funcionen sin trabajo extra.
import { useEffect, useId, useRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';
import { IconClose } from './icons.tsx';

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface ButtonOwnProps { variant?: Variant; size?: Size; block?: boolean }

export function Button({ variant = 'primary', size = 'md', block = false, type = 'button', ...rest }: ButtonOwnProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className="cq-button" data-variant={variant} data-size={size} data-block={block} type={type} {...rest} />;
}

/** Un enlace con aspecto de botón (navegación, no acción). */
export function ButtonLink({ variant = 'primary', size = 'md', block = false, ...rest }: ButtonOwnProps & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return <a className="cq-button" data-variant={variant} data-size={size} data-block={block} {...rest} />;
}

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  hint?: string;
  error?: string | null;
}

export function TextField({ label, hint, error, ...rest }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="cq-field">
      <label className="cq-field-label" htmlFor={id}>{label}</label>
      <input id={id} className="cq-input" aria-invalid={error ? true : undefined} aria-describedby={describedBy} {...rest} />
      {hint ? <span id={hintId} className="cq-field-hint">{hint}</span> : null}
      {error ? <span id={errorId} className="cq-field-error" role="alert">{error}</span> : null}
    </div>
  );
}

export function SelectField({ label, options, value, onChange, required }: {
  label: string; options: readonly string[]; value: string; onChange: (v: string) => void; required?: boolean;
}) {
  const id = useId();
  return (
    <div className="cq-field">
      <label className="cq-field-label" htmlFor={id}>{label}</label>
      <select id={id} className="cq-input" value={value} required={required} onChange={(e) => onChange(e.target.value)}>
        <option value="">Elige una opción</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export function Checkbox({ children, ...rest }: { children: ReactNode } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  return (
    <label className="cq-check">
      <input type="checkbox" {...rest} />
      <span>{children}</span>
    </label>
  );
}

export function Card({ children, className, ...rest }: { children: ReactNode; className?: string } & React.HTMLAttributes<HTMLDivElement>) {
  return <div className={className ? `cq-card ${className}` : 'cq-card'} {...rest}>{children}</div>;
}

export function Chip({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return <span className="cq-chip">{icon}{children}</span>;
}

/** Barra de avance. `label` se lee en voz alta: el ancho solo no informa nada. */
export function Progress({ value, max, label }: { value: number; max: number; label: string }) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className="cq-progress" role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={label}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (next: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} className="cq-toggle" onClick={() => onChange(!checked)}>
      <span>{label}</span>
      <span className="cq-toggle-track" aria-hidden="true"><span className="cq-toggle-knob" /></span>
    </button>
  );
}

/**
 * Hoja modal. Usa <dialog> con showModal(): foco atrapado, Esc para cerrar y fondo inerte, todo nativo.
 * En celular aparece desde abajo; en escritorio, centrada.
 */
export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="cq-sheet"
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="cq-sheet-inner">
        <span className="cq-sheet-handle" aria-hidden="true" />
        <div className="cq-sheet-head">
          <h2 id={titleId} className="cq-sheet-title">{title}</h2>
          <button type="button" className="cq-icon-button" aria-label="Cerrar" onClick={onClose}><IconClose /></button>
        </div>
        {children}
      </div>
    </dialog>
  );
}

export function Skeleton({ width, height }: { width: string; height: string }) {
  return <span className="cq-skeleton" style={{ display: 'block', width, height }} aria-hidden="true" />;
}
